import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { classApi } from '../../../api/classes';
import { getDanceStyleColor, getDanceStyleLabel } from '../../../config/danceStyles';
import './PublicSchool.css';

const PX_PER_HOUR = 60;
const timeToMinutes = t => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + (m || 0);
};

/**
 * PublicSchool - Public-facing landing page for a dance school
 * URL: /school/:orgSlug
 * No auth required. Applies org branding as CSS custom properties.
 */
const PublicSchool = () => {
    const { orgSlug } = useParams();
    const [searchParams] = useSearchParams();
    const termId = searchParams.get('term');
    const [school, setSchool] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const r = await classApi.getPublicSchool(orgSlug, termId);
                if (r.ok) {
                    setSchool(r.data.school);
                } else {
                    setNotFound(true);
                }
            } catch {
                setNotFound(true);
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, [orgSlug, termId]);

    if (isLoading) {
        return <div className="public-school"><p className="public-school-loading">Loading...</p></div>;
    }

    if (notFound || !school) {
        return (
            <div className="public-school">
                <div className="public-school-not-found">
                    <h2>School Not Found</h2>
                    <p>This page does not exist or has been removed.</p>
                </div>
            </div>
        );
    }

    const b = school.branding || {};
    const headerColor = b.headerColor || '#451f78';
    const headerTextColor = b.headerTextColor || '#ffffff';
    const backgroundColor = b.backgroundColor || '#f5f0fb';
    const textColor = b.textColor || '#333333';
    const buttonColor = b.buttonColor || '#451f78';
    const buttonTextColor = b.buttonTextColor || '#ffffff';
    const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Build timetable data for weekly classes, grouped by day → location
    const classes = school.classes || [];
    const dayGroups = [];
    DAY_LABELS.forEach((dayLabel, dayIndex) => {
        const dayClasses = classes.filter(c => c.dayOfWeek === dayIndex);
        if (!dayClasses.length) return;
        const byLocation = {};
        dayClasses.forEach(cls => {
            const loc = cls.roomName || '';
            if (!byLocation[loc]) byLocation[loc] = [];
            byLocation[loc].push(cls);
        });
        const cols = Object.keys(byLocation).sort().map(loc => ({
            location: loc,
            classes: byLocation[loc],
        }));
        dayGroups.push({ day: dayLabel, cols });
    });

    // Compute shared time bounds across all classes
    let gridStartMin = 0;
    let gridEndMin = 0;
    let gridHeightPx = 0;
    let gridHours = [];
    if (classes.length > 0) {
        const allMinutes = classes.flatMap(c => [
            timeToMinutes(c.startTime || '09:00'),
            timeToMinutes(c.endTime || '10:00'),
        ]);
        gridStartMin = Math.floor(Math.min(...allMinutes) / 60) * 60;
        gridEndMin = Math.ceil(Math.max(...allMinutes) / 60) * 60;
        gridHeightPx = (gridEndMin - gridStartMin) * (PX_PER_HOUR / 60);
        gridHours = Array.from(
            { length: (gridEndMin - gridStartMin) / 60 + 1 },
            (_, i) => gridStartMin / 60 + i
        );
    }
    const calcTop = st => (timeToMinutes(st) - gridStartMin) * (PX_PER_HOUR / 60);
    const calcHeight = (st, et) => Math.max((timeToMinutes(et) - timeToMinutes(st)) * (PX_PER_HOUR / 60) - 6, 36);

    return (
        <div className="public-school" style={{ background: backgroundColor, color: textColor }}>
            <div className="public-school-header" style={{ background: headerColor }}>
                {school.logoBase64 && (
                    <img
                        src={school.logoBase64}
                        alt={school.name}
                        className="public-school-logo"
                    />
                )}
                <div className="public-school-header-text">
                    <h1 className="public-school-title" style={{ color: headerTextColor }}>{school.name}</h1>
                    {school.tagline && (
                        <p className="public-school-subtitle" style={{ color: headerTextColor }}>{school.tagline}</p>
                    )}
                </div>
            </div>

            <div className="public-school-body">
                {school.isOpen === false && (
                    <div className="public-school-term-notice" style={{ borderColor: headerColor + '55' }}>
                        <p className="public-school-term-notice-title" style={{ color: headerColor }}>
                            {school.name} isn't taking new enrolments online right now
                        </p>
                        <p className="public-school-term-notice-text">
                            Please check back soon.
                        </p>
                    </div>
                )}

                {school.requestedTermStatus === 'closed' && (
                    <div className="public-school-term-notice" style={{ borderColor: headerColor + '55' }}>
                        <p className="public-school-term-notice-title" style={{ color: headerColor }}>
                            {school.requestedTermName ? `${school.requestedTermName} isn't open for enrolment yet` : "This term isn't open for enrolment yet"}
                        </p>
                        <p className="public-school-term-notice-text">
                            Please check back soon, or send a general enquiry below and we'll let you know as soon as enrolment opens.
                        </p>
                    </div>
                )}

                {school.classes?.length > 0 && (
                    <p className="public-school-instructions">
                        Tap any class below to enquire about it, or use the General Enquiry button at the bottom.
                    </p>
                )}

                {school.groups?.length > 0 && (
                    <>
                        <p className="public-school-section-title">Our Programs</p>
                        <div className="public-school-groups-grid">
                            {school.groups.map(group => (
                                <Link
                                    key={group.id}
                                    to={`/school/${orgSlug}/groups/${group.slug}`}
                                    className="public-school-group-card"
                                    style={{ borderColor: headerColor + '33' }}
                                >
                                    <p className="public-school-group-name" style={{ color: headerColor }}>{group.name}</p>
                                    {group.description && (
                                        <p className="public-school-group-desc">{group.description}</p>
                                    )}
                                </Link>
                            ))}
                        </div>
                    </>
                )}

                {dayGroups.length > 0 && (
                    <>
                        <p className="public-school-section-title">Weekly Classes</p>
                        <div className="public-school-timetable-wrap">
                            {/* Row 1: day + location headers (inset by ruler width) */}
                            <div className="public-school-tt-headers-row">
                                <div className="public-school-tt-ruler-spacer" />
                                {dayGroups.map(({ day, cols }) => (
                                    <div key={day} className="public-school-tt-day-group"
                                        style={{ flex: cols.length, minWidth: 140 * cols.length }}>
                                        <div className="public-school-tt-day-header" style={{ color: headerColor }}>
                                            {day}
                                        </div>
                                        <div className="public-school-tt-location-row">
                                            {cols.map(({ location }) => (
                                                <div key={location} className="public-school-tt-location-header">
                                                    {location || '—'}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {/* Row 2: time ruler + card columns */}
                            <div className="public-school-tt-body-row">
                                <div className="public-school-time-ruler" style={{ height: gridHeightPx }}>
                                    {gridHours.map((hour, i) => (
                                        <div key={hour} className="public-school-time-label"
                                            style={{ top: i * PX_PER_HOUR }}>
                                            {`${String(hour).padStart(2, '0')}:00`}
                                        </div>
                                    ))}
                                </div>
                                {dayGroups.map(({ day, cols }) => (
                                    <div key={day} className="public-school-tt-day-body"
                                        style={{ height: gridHeightPx, flex: cols.length, minWidth: 140 * cols.length }}>
                                        {cols.map(({ location, classes: colClasses }) => (
                                            <div key={location} className="public-school-tt-day-col">
                                                {colClasses.map(cls => (
                                                    <Link
                                                        key={cls.id}
                                                        to={`/school/${orgSlug}/enquire?classId=${cls.id}&className=${encodeURIComponent(cls.name)}`}
                                                        className="public-school-class-card"
                                                        style={{
                                                            top: calcTop(cls.startTime || '09:00'),
                                                            height: calcHeight(cls.startTime || '09:00', cls.endTime || '10:00'),
                                                            borderColor: getDanceStyleColor(cls.style || ''),
                                                        }}
                                                    >
                                                        <p className="public-school-class-card-name">{cls.name}</p>
                                                        <p className="public-school-class-card-detail">
                                                            {cls.startTime}–{cls.endTime}
                                                        </p>
                                                        {cls.level && (
                                                            <p className="public-school-class-card-detail">{cls.level}</p>
                                                        )}
                                                    </Link>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}

                {school.shortCourses?.length > 0 && (
                    <>
                        <p className="public-school-section-title">Short Courses</p>
                        <div className="public-group-classes-list">
                            {school.shortCourses.map(sc => (
                                <div key={sc.id} className="public-group-class-card"
                                    style={{ borderColor: headerColor + '33' }}>
                                    <div className="public-group-class-info">
                                        <p className="public-group-class-name" style={{ color: headerColor }}>{sc.name}</p>
                                        <p className="public-group-class-meta">{sc.style ? getDanceStyleLabel(sc.style) : ''}{sc.level ? ` · ${sc.level}` : ''}</p>
                                        {sc.sessionCount && (
                                            <p className="public-group-class-time">{sc.sessionCount} sessions{sc.startTime ? ` · ${DAY_LABELS[sc.dayOfWeek] || ''} ${sc.startTime}` : ''}</p>
                                        )}
                                        {sc.startDate && (
                                            <p className="public-group-class-time">Starts {new Date(sc.startDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}</p>
                                        )}
                                    </div>
                                    <Link
                                        to={`/school/${orgSlug}/enquire?classId=${sc.id}&className=${encodeURIComponent(sc.name)}${sc.restrictLeaders ? '&pauseLeaders=1' : ''}${sc.restrictFollowers ? '&pauseFollowers=1' : ''}`}
                                        className="public-group-enrol-btn"
                                        style={{ background: buttonColor, color: buttonTextColor }}
                                    >
                                        Enquire
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {school.isOpen !== false && school.requestedTermStatus !== 'closed' && !school.classes?.length && !school.shortCourses?.length && !school.groups?.length && (
                    <p className="public-school-loading">No classes available yet. Check back soon.</p>
                )}

                {school.isOpen !== false && (
                    <Link
                        to={`/school/${orgSlug}/enquire`}
                        className="public-school-enquire-btn"
                        style={{ background: buttonColor, color: buttonTextColor }}
                    >
                        General Enquiry
                    </Link>
                )}
            </div>
            <p className="public-school-powered-by">Powered by Dance Localé</p>
        </div>
    );
};

export default PublicSchool;
